/**
 * Financial Freedom Toolkit — detailed bilingual content for each topic.
 *
 * Each entry expands the brief card on /financial into a full educational
 * resource with `sections` (heading + body or list) and optional `resources`
 * (label + url). Same shape across EN/ES so the render code stays simple.
 */

const en = {
  "emergency-fund": {
    title: "Quietly build an emergency fund",
    short: "Privacy is power. Even $20 hidden is a beginning.",
    sections: [
      { h: "Why this matters",
        body: "Financial control is one of the most common ways an unsafe partner stays in control of you. A small amount of money — quietly, only yours — is freedom planted in soil. You don't need a perfect amount. You need a start." },
      { h: "First steps", list: [
        "Open a bank account in your own name only (online banks like Ally or Capital One 360 can be opened from your phone).",
        "Direct $10–$50 of any income or refund into it — anything you can spare without it being noticed.",
        "Use a separate email he doesn't know about for statements.",
        "Set the bank to paperless. Never let a statement arrive in the mail.",
      ]},
      { h: "Safety notes",
        body: "If you share a tax return, even small amounts get noticed. Cash works too — a hidden envelope at work, with a trusted friend, or in a safety deposit box. Whatever feels safest is the right move." },
      { h: "Resources",
        list: [
          "NNEDV — Financial Safety Tips · https://nnedv.org/content/about-financial-abuse/",
          "WomensLaw — Financial Abuse Info · https://www.womenslaw.org",
        ],
      },
    ],
  },

  "credit-report": {
    title: "Pull your credit report",
    short: "Free, federally protected, and a powerful clarity tool.",
    sections: [
      { h: "Why this matters",
        body: "Financial abusers often open accounts, credit cards, or loans in their partner's name. Many survivors don't learn until years later. Your credit report tells you the truth in one document — and it's free." },
      { h: "Step-by-step",
        list: [
          "Go to AnnualCreditReport.com (this is the only federally authorized free source — accept no substitutes).",
          "Pull all three: Equifax, Experian, TransUnion. You get one free each, every year, from each.",
          "Review every account listed. Don't recognize one? Note it.",
          "If you find an account you didn't open, file an identity theft report at IdentityTheft.gov.",
        ],
      },
      { h: "Red flags",
        list: [
          "Credit cards or loans in your name you don't recognize",
          "Address listed that you've never lived at",
          "Hard inquiries you didn't authorize",
          "A credit score that doesn't match what you'd expect",
        ],
      },
      { h: "Consider a credit freeze",
        body: "A credit freeze locks your credit so no new accounts can be opened in your name. It's free and reversible. Especially valuable if you're leaving — it makes one common form of retaliation impossible." },
      { h: "Resources",
        list: [
          "AnnualCreditReport.com · https://www.annualcreditreport.com",
          "IdentityTheft.gov · https://www.identitytheft.gov",
          "Credit freezes (all three bureaus) · https://www.consumer.ftc.gov/articles/free-security-freeze",
        ],
      },
    ],
  },

  "document-assets": {
    title: "Document your assets",
    short: "Photos and copies, stored somewhere safe — outside the home.",
    sections: [
      { h: "Documents to gather",
        list: [
          "Driver's license / state ID / passport (yours and the children's)",
          "Birth certificates (yours and the children's)",
          "Social Security cards",
          "Marriage license",
          "Tax returns from the past 3 years",
          "Bank statements (most recent 3 months)",
          "Credit card statements",
          "Mortgage / lease / rental agreement",
          "Vehicle titles and registrations",
          "Insurance policies (health, car, life, home)",
          "Medical records and prescription lists",
          "Court orders (custody, protection, divorce)",
          "Photos of jewelry, electronics, valuables",
        ],
      },
      { h: "Storage ideas",
        list: [
          "Encrypted cloud folder (Proton Drive, iCloud, Google Drive) under a new email he doesn't know.",
          "Trusted friend or family member's home — sealed envelope.",
          "Workplace locker or office drawer (if private).",
          "Safety deposit box at a bank not used by the household.",
        ],
      },
      { h: "Safety considerations",
        body: "If you can't safely gather originals, photos of every page work. Phone photos. Backed up to a private cloud. Then delete from your camera roll. Quiet is fine. Quiet is wise." },
      { h: "Resources", list: [
        "Proton Drive (encrypted cloud) · https://proton.me/drive",
        "How to request vital records by state · https://www.cdc.gov/nchs/w2w/index.htm",
        "Social Security card replacement · https://www.ssa.gov/ssnumber/",
      ]},
    ],
  },

  "remote-income": {
    title: "Remote work income ideas",
    short: "Quiet income, your name, your account.",
    sections: [
      { h: "Entry level (start this week)",
        list: [
          "Virtual assistant — Upwork, Belay, Time Etc",
          "Customer support — Working Solutions, LiveOps, Liveworld",
          "Data entry & transcription — Rev, GoTranscript, TranscribeMe",
          "Online surveys & user testing — UserTesting, Prolific (supplementary, not a replacement)",
        ],
      },
      { h: "Intermediate (with some experience)",
        list: [
          "Bookkeeping — Bench, QuickBooks Live (training available)",
          "Social media management — small businesses near you, freelance sites",
          "Freelance writing — Contently, Compose.ly, niche blogs",
          "Tutoring — Wyzant, Varsity Tutors, online ESL platforms",
        ],
      },
      { h: "Advanced (your skills, no boss)",
        list: [
          "Design — Dribbble, 99designs, freelance",
          "Web development — Toptal, Arc, freelance",
          "Consulting in your field — LinkedIn outreach to past colleagues",
          "Specialized writing (legal, medical, technical) — premium freelance rates",
        ],
      },
      { h: "Training & resources",
        list: [
          "Coursera & Google Career Certificates · https://grow.google",
          "Domestic Violence Hotline financial empowerment resources · https://www.thehotline.org",
          "FreeFrom — survivor-led financial recovery · https://www.freefrom.org",
        ],
      },
    ],
  },

  "apartment-starter": {
    title: "Apartment starter list",
    short: "A safe home does not need to be a perfect home.",
    sections: [
      { h: "First-week essentials",
        list: [
          "Mattress or air mattress + sheets, pillow, blanket",
          "Phone charger",
          "Toilet paper, soap, a towel",
          "A pot, a pan, two plates, two cups, one knife, one cutting board",
          "Trash bags",
          "Lamp or string lights (overhead lighting is harsh after a hard day)",
        ],
      },
      { h: "Kitchen basics",
        list: [
          "Salt, pepper, olive oil, butter",
          "Coffee or tea — whatever your morning needs to feel like yours",
          "A few favorite snacks (you're allowed to be comforted)",
          "Reusable water bottle",
        ],
      },
      { h: "Bathroom basics",
        list: [
          "Shower curtain + rings",
          "Shampoo, conditioner, body wash",
          "Toothbrush, toothpaste",
          "First aid: ibuprofen, bandages, tissues",
        ],
      },
      { h: "Things that can wait",
        list: [
          "Matching dishes",
          "A couch",
          "Wall art",
          "A full pantry",
          "Anything that feels like 'finishing' the home",
        ],
      },
      { h: "Encouragement",
        body: "A safe home does not need to be a perfect home. The first night you sleep without bracing for the door is the foundation. The rest fills in slowly. You are not behind. You are beginning." },
      { h: "Resources",
        list: [
          "Buy Nothing Project · https://buynothingproject.org",
          "Freecycle · https://www.freecycle.org",
          "Local DV shelters often have starter-home programs even after you leave (call the National Hotline 1-800-799-7233 for a referral · https://www.thehotline.org)",
        ],
      },
    ],
  },

  "free-legal-help": {
    title: "Free legal help",
    short: "You don't have to navigate this alone — and you don't have to pay first.",
    sections: [
      { h: "Common legal topics",
        list: [
          "Protection orders (restraining orders)",
          "Custody and parenting plans",
          "Divorce and separation",
          "Housing rights — including breaking a lease for DV reasons",
          "Immigration relief (U-Visa, VAWA self-petition)",
          "Workplace accommodations and protections",
        ],
      },
      { h: "Questions worth asking",
        list: [
          "Do you handle domestic violence cases pro bono or sliding scale?",
          "How long do protection orders take in this county?",
          "What evidence is most helpful for my situation?",
          "What happens if he violates a protection order?",
          "Can custody be filed at the same time as a protection order?",
        ],
      },
      { h: "Finding help",
        list: [
          "LawHelp.org — find free legal aid by state",
          "WomensLaw.org — state-by-state laws explained in plain language",
          "National DV Hotline (1-800-799-7233) — they can refer you to legal advocates",
          "Local bar association lawyer referral lines often have free first consultations",
          "Domestic violence shelters typically have legal advocates on staff or on call",
        ],
      },
      { h: "Important to know",
        body: "Many states have civil legal aid funded specifically for survivors. You do not have to be in a shelter to qualify. You do not have to have left to qualify. Talking to an advocate is free, confidential, and creates no legal record on its own." },
      { h: "Resources",
        list: [
          "WomensLaw — https://www.womenslaw.org",
          "LawHelp — https://www.lawhelp.org",
          "National Domestic Violence Hotline — 1-800-799-7233",
          "RAINN — 1-800-656-4673",
        ],
      },
    ],
  },
};

const es = {
  "emergency-fund": {
    title: "Construye un fondo de emergencia, en silencio",
    short: "La privacidad es poder. Incluso $20 escondidos son un comienzo.",
    sections: [
      { h: "Por qué importa",
        body: "El control financiero es una de las formas más comunes en que una pareja insegura te mantiene bajo control. Una pequeña cantidad de dinero — en silencio, solo tuyo — es libertad sembrada. No necesitas una cantidad perfecta. Necesitas empezar." },
      { h: "Primeros pasos", list: [
        "Abre una cuenta bancaria solo a tu nombre (bancos en línea como Ally o Capital One 360 se pueden abrir desde tu teléfono).",
        "Deposita $10–$50 de cualquier ingreso o reembolso — lo que puedas sin que se note.",
        "Usa un correo aparte que él no conozca para los estados de cuenta.",
        "Configura el banco en modo digital. Que nunca llegue un estado de cuenta por correo postal.",
      ]},
      { h: "Notas de seguridad",
        body: "Si comparten declaración de impuestos, incluso cantidades pequeñas se pueden notar. El efectivo también funciona — un sobre escondido en el trabajo, con una amiga de confianza, o en una caja de seguridad bancaria. Lo que se sienta más seguro es la opción correcta." },
      { h: "Recursos", list: [
        "NNEDV — Consejos de Seguridad Financiera · https://nnedv.org/content/about-financial-abuse/",
        "WomensLaw — Información sobre Abuso Financiero · https://www.womenslaw.org",
      ]},
    ],
  },

  "credit-report": {
    title: "Revisa tu reporte de crédito",
    short: "Gratuito, protegido federalmente, y una herramienta poderosa de claridad.",
    sections: [
      { h: "Por qué importa",
        body: "Los abusadores financieros frecuentemente abren cuentas, tarjetas de crédito o préstamos a nombre de su pareja. Muchas sobrevivientes no se enteran hasta años después. Tu reporte de crédito te dice la verdad en un solo documento — y es gratis." },
      { h: "Paso a paso", list: [
        "Ve a AnnualCreditReport.com (es la única fuente federalmente autorizada — no aceptes sustitutos).",
        "Pide los tres: Equifax, Experian, TransUnion. Tienes uno gratis al año de cada uno.",
        "Revisa cada cuenta listada. ¿No reconoces alguna? Anótala.",
        "Si encuentras una cuenta que tú no abriste, presenta un reporte de robo de identidad en IdentityTheft.gov.",
      ]},
      { h: "Señales de alerta", list: [
        "Tarjetas o préstamos a tu nombre que no reconoces",
        "Dirección que nunca has tenido",
        "Consultas duras que tú no autorizaste",
        "Una puntuación de crédito que no coincide con lo que esperarías",
      ]},
      { h: "Considera un congelamiento de crédito",
        body: "Un congelamiento bloquea tu crédito para que nadie pueda abrir cuentas nuevas a tu nombre. Es gratis y reversible. Especialmente valioso si estás planeando salir — hace imposible una forma común de represalia." },
      { h: "Recursos", list: [
        "AnnualCreditReport.com · https://www.annualcreditreport.com",
        "IdentityTheft.gov · https://www.identitytheft.gov",
        "Congelamientos de crédito (las tres agencias) · https://www.consumer.ftc.gov/articles/free-security-freeze",
      ]},
    ],
  },

  "document-assets": {
    title: "Documenta tus bienes",
    short: "Fotos y copias, guardadas en un lugar seguro — fuera de la casa.",
    sections: [
      { h: "Documentos para reunir", list: [
        "Licencia de conducir / identificación estatal / pasaporte (tuyo y de los hijos)",
        "Actas de nacimiento (tuya y de los hijos)",
        "Tarjetas del Seguro Social",
        "Licencia de matrimonio",
        "Declaraciones de impuestos de los últimos 3 años",
        "Estados de cuenta bancarios (los últimos 3 meses)",
        "Estados de tarjetas de crédito",
        "Hipoteca / contrato de renta",
        "Títulos y registros de vehículos",
        "Pólizas de seguro (salud, auto, vida, casa)",
        "Récords médicos y lista de medicamentos",
        "Órdenes judiciales (custodia, protección, divorcio)",
        "Fotos de joyería, electrónicos, objetos de valor",
      ]},
      { h: "Ideas para guardarlos", list: [
        "Carpeta encriptada en la nube (Proton Drive, iCloud, Google Drive) bajo un correo nuevo que él no conozca.",
        "Casa de una amiga o familiar de confianza — sobre sellado.",
        "Casillero del trabajo o cajón privado de la oficina.",
        "Caja de seguridad bancaria en un banco que la casa no use.",
      ]},
      { h: "Consideraciones de seguridad",
        body: "Si no puedes reunir originales con seguridad, las fotos de cada página funcionan. Fotos del teléfono. Respaldadas en una nube privada. Luego bórralas del rollo de cámara. Está bien hacerlo en silencio. Es sabio." },
      { h: "Recursos", list: [
        "Proton Drive (nube encriptada) · https://proton.me/drive",
        "Cómo solicitar documentos vitales por estado · https://www.cdc.gov/nchs/w2w/index.htm",
        "Reemplazo de tarjeta del Seguro Social · https://www.ssa.gov/ssnumber/",
      ]},
    ],
  },

  "remote-income": {
    title: "Ideas de ingreso remoto",
    short: "Ingreso silencioso, a tu nombre, en tu cuenta.",
    sections: [
      { h: "Nivel inicial (empieza esta semana)", list: [
        "Asistente virtual — Upwork, Belay, Time Etc",
        "Atención al cliente — Working Solutions, LiveOps, Liveworld",
        "Captura de datos y transcripción — Rev, GoTranscript, TranscribeMe",
        "Encuestas y pruebas de usuario — UserTesting, Prolific (complementario, no un reemplazo)",
      ]},
      { h: "Nivel intermedio (con algo de experiencia)", list: [
        "Contabilidad básica — Bench, QuickBooks Live (con capacitación)",
        "Manejo de redes sociales — negocios pequeños cerca de ti, sitios freelance",
        "Escritura freelance — Contently, Compose.ly, blogs de nicho",
        "Tutorías — Wyzant, Varsity Tutors, plataformas de ESL en línea",
      ]},
      { h: "Nivel avanzado (tus habilidades, sin jefe)", list: [
        "Diseño — Dribbble, 99designs, freelance",
        "Desarrollo web — Toptal, Arc, freelance",
        "Consultoría en tu campo — LinkedIn a colegas anteriores",
        "Escritura especializada (legal, médica, técnica) — tarifas premium",
      ]},
      { h: "Capacitación y recursos", list: [
        "Coursera y Google Career Certificates · https://grow.google",
        "Recursos de empoderamiento financiero de la Línea Nacional · https://www.thehotline.org",
        "FreeFrom — recuperación financiera liderada por sobrevivientes · https://www.freefrom.org",
      ]},
    ],
  },

  "apartment-starter": {
    title: "Lista para un nuevo departamento",
    short: "Una casa segura no necesita ser una casa perfecta.",
    sections: [
      { h: "Esenciales de la primera semana", list: [
        "Colchón o colchón inflable + sábanas, almohada, cobija",
        "Cargador de teléfono",
        "Papel de baño, jabón, una toalla",
        "Una olla, una sartén, dos platos, dos vasos, un cuchillo, una tabla",
        "Bolsas de basura",
        "Lámpara o luces de cadena (la luz del techo es dura después de un día difícil)",
      ]},
      { h: "Cocina básica", list: [
        "Sal, pimienta, aceite de oliva, mantequilla",
        "Café o té — lo que tu mañana necesite para sentirse tuya",
        "Algunos snacks favoritos (tienes permiso de consolarte)",
        "Botella de agua reutilizable",
      ]},
      { h: "Baño básico", list: [
        "Cortina de baño + ganchos",
        "Shampoo, acondicionador, jabón corporal",
        "Cepillo y pasta de dientes",
        "Botiquín: ibuprofeno, curitas, pañuelos",
      ]},
      { h: "Cosas que pueden esperar", list: [
        "Vajilla que combine",
        "Un sofá",
        "Arte para las paredes",
        "Una despensa completa",
        "Cualquier cosa que se sienta como 'terminar' la casa",
      ]},
      { h: "Aliento",
        body: "Una casa segura no necesita ser una casa perfecta. La primera noche que duermes sin prepararte para escuchar la puerta es la base. El resto se llena lentamente. No estás atrasada. Estás empezando." },
      { h: "Recursos", list: [
        "Buy Nothing Project · https://buynothingproject.org",
        "Freecycle · https://www.freecycle.org",
        "Los refugios locales de VD frecuentemente tienen programas de hogar inicial incluso después de salir (llama a la Línea Nacional 1-800-799-7233 para una referencia · https://www.thehotline.org)",
      ]},
    ],
  },

  "free-legal-help": {
    title: "Apoyo legal gratuito",
    short: "No tienes que navegar esto sola — y no tienes que pagar primero.",
    sections: [
      { h: "Temas legales comunes", list: [
        "Órdenes de protección (órdenes de alejamiento)",
        "Custodia y planes de crianza",
        "Divorcio y separación",
        "Derechos de vivienda — incluyendo romper un contrato de renta por razones de VD",
        "Alivio migratorio (Visa U, autopetición VAWA)",
        "Acomodaciones y protecciones en el trabajo",
      ]},
      { h: "Preguntas que vale la pena hacer", list: [
        "¿Atiende casos de violencia doméstica pro bono o con tarifa escalonada?",
        "¿Cuánto tardan las órdenes de protección en este condado?",
        "¿Qué evidencia es más útil para mi situación?",
        "¿Qué pasa si él viola la orden de protección?",
        "¿Se puede pedir custodia al mismo tiempo que la orden de protección?",
      ]},
      { h: "Cómo encontrar ayuda", list: [
        "LawHelp.org — encuentra ayuda legal gratuita por estado",
        "WomensLaw.org — leyes estado por estado en lenguaje claro",
        "Línea Nacional de VD (1-800-799-7233) — pueden referirte a defensoras legales",
        "Las asociaciones locales de abogados frecuentemente tienen consultas iniciales gratis",
        "Los refugios de VD típicamente tienen defensoras legales en el equipo o disponibles",
      ]},
      { h: "Importante saber",
        body: "Muchos estados tienen ayuda legal civil financiada específicamente para sobrevivientes. No tienes que estar en un refugio para calificar. No tienes que haber salido para calificar. Hablar con una defensora es gratis, confidencial y no crea un registro legal por sí solo." },
      { h: "Recursos", list: [
        "WomensLaw — https://www.womenslaw.org",
        "LawHelp — https://www.lawhelp.org",
        "Línea Nacional de Violencia Doméstica — 1-800-799-7233",
        "RAINN (línea con español) — 1-800-656-4673",
      ]},
    ],
  },
};

const REGISTRY = { en, es };

export function getFinancialDetail(slug, lang) {
  const bundle = REGISTRY[lang] || REGISTRY.en;
  return bundle[slug] || null;
}

/** Ordered list of slugs for the /financial index page. */
export const FINANCIAL_SLUGS = [
  "emergency-fund",
  "credit-report",
  "document-assets",
  "remote-income",
  "apartment-starter",
  "free-legal-help",
];

/** Short cards for the /financial index — title + short, bilingual. */
export function getFinancialIndex(lang) {
  const bundle = REGISTRY[lang] || REGISTRY.en;
  return FINANCIAL_SLUGS.map((slug) => ({
    slug,
    title: bundle[slug].title,
    short: bundle[slug].short,
  }));
}
